
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { db } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	DEFAULT_KIT_ICON,
	KIT_SURFACES,
	KIT_TOOL_LABEL_PRESETS,
	KIT_TYPES,
	KitToolLabel,
	KitToolLabelPresetKey,
	NormalizedRunnitKit as RunnitKit,
	NormalizedRunnitKitTool as RunnitKitTool,
	RunnitNodeDef,
	customKitToolLabelId,
	deriveKitTags,
	normalizeKit,
} from '@run-diffusion/shared';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';

const NODE_DEF_BATCH_SIZE = 500;

// The two admin-only tabs that sit after the derived Kit Tags.
const TAB_NOT_VISIBLE = '__notVisible';
const TAB_ALL = '__all';

const SURFACE_ICONS: Record<string, string> = {
	web: 'mdi-web',
	photoshop: 'mdi-adobe',
	miro: 'mdi-view-dashboard-outline',
	revit: 'mdi-floor-plan',
	rhino: 'mdi-cube-outline',
	omniverse: 'mdi-rotate-3d-variant',
};

// Offered in the inspector after the Kit's own vocabulary.
const COMMON_KIT_TAGS: string[] = ['Featured', 'Most Powerful', 'Everyday Use', 'Fast & Affordable', 'Other'];

const AVATAR_BACKGROUNDS: string[] = [
	'linear-gradient(135deg, #7532d3, #c750ce)',
	'linear-gradient(135deg, #5a61ed, #9968df)',
	'linear-gradient(135deg, #c750ce, #ac32b2)',
	'linear-gradient(135deg, #1822dc, #5a61ed)',
	'linear-gradient(135deg, #9968df, #a3a7f5)',
];

/**
 * Admin > Manage Kits.
 *
 * Two levels: a Kit grid whose cards render like the user-facing "Choose a
 * Generation Kit" dialog, and a Kit detail view whose tabs ARE the Kit's Kit
 * Tags, plus a tool inspector.
 *
 * Kit Tags stay DERIVED (the shared resolver's rule: Featured first, the rest
 * alphabetical, Other last) rather than a stored vocabulary, because that is
 * how every surface consumes them. Renaming or deleting a tag is therefore a
 * bulk edit across the Kit's tools, after which the tab re-derives. A tag with
 * no tools cannot exist, so there is no "create an empty tab": the add-tool
 * flow pre-tags with the active tab instead.
 *
 * Reads Firestore directly rather than the store binding, because an admin has
 * to see drafts, unpublished tools, and curated ids with no catalogue entry at
 * all. Every tool edit is a whole-array read-modify-write, since `tools` is a
 * Firestore array with no element paths.
 */
export default Vue.extend({
	name: 'AdminManageKits',
	components: { RunnitImage },
	data () {
		return {
			KIT_SURFACES,
			KIT_TOOL_LABEL_PRESETS,
			SURFACE_ICONS,
			TAB_NOT_VISIBLE,
			TAB_ALL,
			loading: true as boolean,
			saving: false as boolean,
			saveState: 'idle' as string,
			toast: '' as string,
			kits: [] as RunnitKit[],
			nodeDefs: [] as RunnitNodeDef[],
			mode: 'admin' as string,
			selectedKitId: null as string | null,
			activeTab: null as string | null,
			settingsOpen: false as boolean,
			inspectId: null as string | null,
			query: '' as string,
			pickerOpen: false as boolean,
			pickerQuery: '' as string,
			kitDragId: null as string | null,
			toolDragId: null as string | null,
			savedTimer: null as any,
			toastTimer: null as any,
		};
	},
	computed: {
		...mapState(['user']),
		adminMode (): boolean {
			return this.mode === 'admin';
		},
		labelPresetKeys (): string[] {
			return Object.keys(KIT_TOOL_LABEL_PRESETS);
		},
		saveIcon (): string {
			if (this.saveState === 'saving') return 'mdi-loading';
			if (this.saveState === 'saved') return 'mdi-check-circle-outline';
			return 'mdi-cloud-check-outline';
		},
		saveLabel (): string {
			if (this.saveState === 'saving') return 'Saving';
			if (this.saveState === 'saved') return 'Saved';
			return 'All changes saved';
		},
		nodeDefsMap (): Record<string, RunnitNodeDef> {
			return this.nodeDefs.reduce((acc: Record<string, RunnitNodeDef>, nodeDef: RunnitNodeDef) => {
				acc[nodeDef.id] = nodeDef;
				return acc;
			}, {});
		},
		sortedKits (): RunnitKit[] {
			return [...this.kits].sort((a: RunnitKit, b: RunnitKit) => (
				(a.sortOrder || 0) - (b.sortOrder || 0)
				|| KIT_TYPES.indexOf(a.id) - KIT_TYPES.indexOf(b.id)
			));
		},
		/** The grid honours User preview: drafts and soft-deleted Kits drop out. */
		gridKits (): RunnitKit[] {
			return this.sortedKits.filter((kit: RunnitKit) => (
				!kit.isDeleted && (this.adminMode || kit.isPublished)
			));
		},
		deletedKits (): RunnitKit[] {
			return this.sortedKits.filter((kit: RunnitKit) => kit.isDeleted);
		},
		creatableKitTypes (): string[] {
			const existing = this.kits.map((kit: RunnitKit) => kit.id);
			return KIT_TYPES.filter((kitType: string) => existing.indexOf(kitType) < 0);
		},
		selectedKit (): RunnitKit | null {
			if (!this.selectedKitId) return null;
			return this.kits.find((kit: RunnitKit) => kit.id === this.selectedKitId) || null;
		},
		/**
		 * Every curated entry with the state an admin needs: whether its
		 * catalogue entry exists, whether users can reach it, and where it sits
		 * in the curated order.
		 */
		entries (): any[] {
			if (!this.selectedKit) return [];
			const total = this.selectedKit.tools.length;
			return this.selectedKit.tools.map((tool: RunnitKitTool, index: number) => {
				const nodeDef: any = this.nodeDefsMap[tool.nodeDefId] || null;
				const tagged = !!Object.keys(tool.tags || {}).length;
				const unpublished = !!nodeDef && nodeDef.isPublished === false;

				let flag = '';
				let flagKind = '';
				if (!nodeDef) {
					flag = 'No tool in the catalogue has this id, so users see nothing here.';
					flagKind = 'danger';
				} else if (unpublished) {
					flag = 'This tool is unpublished, so users cannot see it in the Kit.';
					flagKind = 'warn';
				} else if (!tagged) {
					flag = 'No Kit Tags, so it sits under no tab and users cannot reach it.';
					flagKind = 'warn';
				}

				return {
					tool,
					index,
					nodeDef,
					avatar: nodeDef && nodeDef.avatar && typeof nodeDef.avatar !== 'string' ? nodeDef.avatar : null,
					title: nodeDef ? (nodeDef.title || tool.nodeDefId) : tool.nodeDefId,
					description: nodeDef ? (nodeDef.description || '') : 'Missing from the tool catalogue',
					eta: this.formatEta(nodeDef),
					credits: nodeDef && nodeDef.costPerResult ? String(nodeDef.costPerResult) : '',
					isNew: this.isNew(nodeDef),
					position: (index + 1) + '/' + total,
					visibleToUsers: !!nodeDef && !unpublished && tagged,
					flag,
					flagKind,
				};
			});
		},
		/** The tab row: derived Kit Tags, then the two admin-only tabs. */
		tabs (): any[] {
			if (!this.selectedKit) return [];
			// deriveKitTags only reads `.tags`, so the raw curation can drive it.
			// That keeps the ordering rule (Featured first, rest alphabetical,
			// Other last) in the shared resolver rather than duplicated here.
			const source = this.adminMode
				? this.selectedKit.tools
				: this.entries.filter((entry: any) => entry.visibleToUsers).map((entry: any) => entry.tool);

			const derived = deriveKitTags(source as any[]).map((tag: any) => ({
				key: tag.key,
				name: tag.label,
				icon: '',
				meta: false,
				count: this.entries.filter((entry: any) => (
					entry.tool.tags[tag.key] && (this.adminMode || entry.visibleToUsers)
				)).length,
			}));

			if (!this.adminMode) return derived;

			const notVisible = this.entries.filter((entry: any) => !entry.visibleToUsers).length;
			return derived.concat([
				{ key: TAB_NOT_VISIBLE, name: 'Not visible', icon: 'mdi-eye-off-outline', meta: true, count: notVisible },
				{ key: TAB_ALL, name: 'All curated', icon: 'mdi-format-list-bulleted', meta: true, count: this.entries.length },
			]);
		},
		activeTagTab (): string | null {
			if (!this.activeTab || this.activeTab === TAB_NOT_VISIBLE || this.activeTab === TAB_ALL) return null;
			return this.activeTab;
		},
		visibleEntries (): any[] {
			let list = this.entries;

			if (!this.adminMode) {
				list = list.filter((entry: any) => entry.visibleToUsers);
			}

			if (this.activeTab === TAB_NOT_VISIBLE) {
				list = list.filter((entry: any) => !entry.visibleToUsers);
			} else if (this.activeTab && this.activeTab !== TAB_ALL) {
				list = list.filter((entry: any) => !!entry.tool.tags[this.activeTab]);
			}

			const q = (this.query || '').trim().toLowerCase();
			if (q) {
				list = list.filter((entry: any) => (
					(entry.title + ' ' + entry.description).toLowerCase().indexOf(q) >= 0
				));
			}
			return list;
		},
		inspectEntry (): any {
			if (!this.inspectId) return null;
			return this.entries.find((entry: any) => entry.tool.nodeDefId === this.inspectId) || null;
		},
		/** The Kit's own vocabulary first, then the common tags. */
		inspectorTagOptions (): string[] {
			const inKit: string[] = [];
			(this.selectedKit ? this.selectedKit.tools : []).forEach((tool: RunnitKitTool) => {
				Object.keys(tool.tags || {}).forEach((key: string) => {
					if (inKit.indexOf(key) < 0) inKit.push(key);
				});
			});
			return inKit.concat(COMMON_KIT_TAGS.filter((key: string) => inKit.indexOf(key) < 0));
		},
		pickerCandidates (): RunnitNodeDef[] {
			if (!this.selectedKit) return [];
			const inKit = this.selectedKit.tools.map((tool: RunnitKitTool) => tool.nodeDefId);
			const q = (this.pickerQuery || '').trim().toLowerCase();
			return this.nodeDefs
				.filter((nodeDef: RunnitNodeDef) => inKit.indexOf(nodeDef.id) < 0)
				.filter((nodeDef: any) => (
					!q || ((nodeDef.title || '') + ' ' + (nodeDef.description || '')).toLowerCase().indexOf(q) >= 0
				))
				.sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''))
				.slice(0, 60);
		},
		tabNote (): string {
			if (!this.adminMode) return '';
			if (this.activeTab === TAB_NOT_VISIBLE) {
				return 'These curated entries never reach a user: no Kit Tags, or the catalogue entry is missing or unpublished. Tag them, or remove them.';
			}
			if (this.activeTab === TAB_ALL) {
				return 'Every curated entry in Kit order. That order is what users see inside each tab.';
			}
			return '';
		},
		tabNoteKind (): string {
			return this.activeTab === TAB_NOT_VISIBLE ? 'warn' : 'info';
		},
		emptyNote (): string {
			if (this.query) return 'No tools match that search.';
			if (this.activeTab === TAB_NOT_VISIBLE) return 'Every curated tool in this Kit is reachable by users.';
			return this.adminMode ? 'Nothing under this tab yet. Add a tool, or tag one.' : 'Nothing here yet.';
		},
		footerNote (): string {
			if (!this.adminMode) return 'User preview hides draft Kits, untagged tools, and anything unpublished or missing.';
			const tag = this.activeTagTab;
			if (tag) {
				return 'Drag a card to change the curated order, which is what users see inside every tab. Use the pencil on the "' + tag + '" tab to rename that Kit Tag across every tool, or the bin to remove it. Every edit saves immediately.';
			}
			return 'Drag a card to change the curated order, which is what users see inside every tab. Every edit saves immediately.';
		},
	},
	watch: {
		selectedKitId () {
			this.inspectId = null;
			this.query = '';
			this.$nextTick(this.setDefaultTab);
		},
		mode () {
			this.inspectId = null;
			this.$nextTick(this.setDefaultTab);
		},
	},
	mounted () {
		this.initialize();
	},
	beforeDestroy () {
		clearTimeout(this.savedTimer);
		clearTimeout(this.toastTimer);
	},
	methods: {
		...mapActions(['updateSnackbar']),
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		async initialize () {
			this.loading = true;
			try {
				await Promise.all([this.fetchKits(), this.fetchNodeDefs()]);
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to load Kits.');
			} finally {
				this.loading = false;
			}
		},
		async fetchKits () {
			const snap = await db.collection('runnitKits').get();
			this.kits = snap.docs
				.map((doc: any) => normalizeKit(doc.data(), doc.id))
				.filter((kit: RunnitKit | null) => !!kit) as RunnitKit[];
		},
		async fetchNodeDefs () {
			const out: RunnitNodeDef[] = [];
			let lastDoc: any = null;
			// eslint-disable-next-line no-constant-condition
			while (true) {
				let query: any = db.collection('runnitNodeDefs')
					.where('isDeleted', '==', false)
					.limit(NODE_DEF_BATCH_SIZE);
				if (lastDoc) query = query.startAfter(lastDoc);
				const snap = await query.get();
				snap.docs.forEach((doc: any) => out.push({ ...doc.data(), get id () { return doc.id; } } as RunnitNodeDef));
				if (snap.docs.length < NODE_DEF_BATCH_SIZE) break;
				lastDoc = snap.docs[snap.docs.length - 1];
			}
			this.nodeDefs = out;
		},

		// ── display helpers ──
		toolTitle (nodeDefId: string): string {
			const nodeDef: any = this.nodeDefsMap[nodeDefId];
			return (nodeDef && nodeDef.title) || nodeDefId;
		},
		initials (name: string): string {
			return (name || '')
				.replace(/[^A-Za-z0-9 ]/g, '')
				.split(' ')
				.filter(Boolean)
				.slice(0, 2)
				.map((word: string) => word[0])
				.join('')
				.toUpperCase();
		},
		avatarBg (nodeDefId: string): string {
			let sum = 0;
			const id = nodeDefId || '';
			for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
			return AVATAR_BACKGROUNDS[sum % AVATAR_BACKGROUNDS.length];
		},
		formatEta (nodeDef: any): string {
			const seconds = nodeDef && nodeDef.averageGenInSeconds;
			if (!seconds) return '';
			if (seconds >= 60) return (Math.round((seconds / 60) * 10) / 10) + 'm';
			return Math.round(seconds) + 's';
		},
		isNew (nodeDef: any): boolean {
			const newUntil = nodeDef && nodeDef.newUntil;
			if (!newUntil) return false;
			let asDate: Date;
			if (newUntil instanceof Date) asDate = newUntil;
			else if (newUntil.toMillis) asDate = new Date(newUntil.toMillis());
			else if (newUntil.toDate) asDate = newUntil.toDate();
			else return false;
			return new Date() < asDate;
		},
		/** Curated entries a user cannot reach: missing, unpublished, or untagged. */
		issueCount (kit: RunnitKit): number {
			return kit.tools.filter((tool: RunnitKitTool) => {
				const nodeDef: any = this.nodeDefsMap[tool.nodeDefId];
				if (!nodeDef || nodeDef.isPublished === false) return true;
				return !Object.keys(tool.tags || {}).length;
			}).length;
		},
		kitMeta (kit: RunnitKit): string {
			const tabCount = deriveKitTags(kit.tools as any[]).length;
			const surfaces = kit.surfaces && Object.keys(kit.surfaces).length
				? Object.keys(kit.surfaces).join(', ')
				: 'all surfaces';
			return kit.tools.length + ' tools · ' + tabCount + ' tabs · ' + surfaces;
		},
		hasExplicitSurfaces (kit: RunnitKit): boolean {
			return !!(kit.surfaces && Object.keys(kit.surfaces).length);
		},
		isSurfaceOn (kit: RunnitKit, surface: string): boolean {
			return !!(kit.surfaces && kit.surfaces[surface]);
		},
		hasLabel (tool: RunnitKitTool, labelId: string): boolean {
			return (tool.labels || []).some((label: KitToolLabel) => label.id === labelId);
		},
		customLabels (tool: RunnitKitTool): KitToolLabel[] {
			const presetIds = Object.keys(KIT_TOOL_LABEL_PRESETS)
				.map((key: string) => KIT_TOOL_LABEL_PRESETS[key as KitToolLabelPresetKey].id);
			return (tool.labels || []).filter((label: KitToolLabel) => presetIds.indexOf(label.id) < 0);
		},
		alsoCuratedIn (nodeDefId: string): any[] {
			const out: any[] = [];
			this.kits.forEach((kit: RunnitKit) => {
				if (kit.id === this.selectedKitId) return;
				const tool = kit.tools.find((entry: RunnitKitTool) => entry.nodeDefId === nodeDefId);
				if (tool) out.push({ kit, tags: Object.keys(tool.tags || {}).join(', ') });
			});
			return out;
		},

		// ── navigation ──
		openKit (kit: RunnitKit, withSettings: boolean = false) {
			this.selectedKitId = kit.id;
			this.settingsOpen = !!withSettings;
		},
		closeKit () {
			this.selectedKitId = null;
			this.settingsOpen = false;
		},
		setDefaultTab () {
			const first = this.tabs[0];
			this.activeTab = first ? first.key : null;
		},
		inspect (nodeDefId: string) {
			this.inspectId = this.inspectId === nodeDefId ? null : nodeDefId;
		},
		onToolClick (entry: any) {
			if (this.adminMode) this.inspect(entry.tool.nodeDefId);
		},
		openPicker () {
			this.pickerOpen = true;
			this.pickerQuery = '';
			this.$nextTick(() => {
				const input: any = this.$refs.pickerInput;
				if (input && input.focus) input.focus();
			});
		},

		// ── drag to reorder ──
		onKitDragStart (kit: RunnitKit) {
			if (!this.adminMode) return;
			this.kitDragId = kit.id;
		},
		async onKitDrop (target: RunnitKit) {
			const sourceId = this.kitDragId;
			this.kitDragId = null;
			if (!sourceId || sourceId === target.id) return;

			const ordered = this.gridKits.slice();
			const from = ordered.findIndex((kit: RunnitKit) => kit.id === sourceId);
			const to = ordered.findIndex((kit: RunnitKit) => kit.id === target.id);
			if (from < 0 || to < 0) return;
			ordered.splice(to, 0, ordered.splice(from, 1)[0]);

			this.beginSave('Reordered the Kits');
			try {
				// Dense renumber, so the order never depends on the Kit Type
				// declaration-order tiebreak.
				await Promise.all(ordered.map((kit: RunnitKit, index: number) => (
					db.doc('runnitKits/' + kit.id).update({ sortOrder: index })
				)));
				await this.fetchKits();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to reorder the Kits.');
			} finally {
				this.endSave();
			}
		},
		onToolDragStart (tool: RunnitKitTool) {
			if (!this.adminMode) return;
			this.toolDragId = tool.nodeDefId;
		},
		async onToolDrop (target: RunnitKitTool) {
			const sourceId = this.toolDragId;
			this.toolDragId = null;
			if (!sourceId || !this.selectedKit || sourceId === target.nodeDefId) return;

			const tools = this.selectedKit.tools.slice();
			const from = tools.findIndex((tool: RunnitKitTool) => tool.nodeDefId === sourceId);
			const to = tools.findIndex((tool: RunnitKitTool) => tool.nodeDefId === target.nodeDefId);
			if (from < 0 || to < 0) return;
			tools.splice(to, 0, tools.splice(from, 1)[0]);
			await this.saveTools(this.selectedKit, tools, 'Reordered the tools', 'Failed to reorder the tools.');
		},

		// ── Kit CRUD ──
		async createKit (kitType: string) {
			this.beginSave('Created the ' + kitType + ' Kit as a draft');
			try {
				await db.doc('runnitKits/' + kitType).set({
					id: kitType,
					label: kitType,
					icon: DEFAULT_KIT_ICON,
					description: '',
					sortOrder: this.kits.length,
					// New Kits start as drafts so a half-curated Kit is never live.
					isPublished: false,
					publishedAt: null,
					isDeleted: false,
					deletedAt: null,
					defaultNodeDefId: null,
					surfaces: null,
					tools: [],
				});
				await this.fetchKits();
				this.selectedKitId = kitType;
				this.settingsOpen = true;
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to create the Kit.');
			} finally {
				this.endSave();
			}
		},
		async renameKit (kit: RunnitKit, label: string) {
			const trimmed = (label || '').trim();
			if (!trimmed || trimmed === kit.label) return;
			await this.updateKit(kit, { label: trimmed }, 'Renamed the Kit to ' + trimmed, 'Failed to rename the Kit.');
		},
		async setKitIcon (kit: RunnitKit, icon: string) {
			const trimmed = (icon || '').trim();
			if (!trimmed || trimmed === kit.icon) return;
			await this.updateKit(kit, { icon: trimmed }, 'Changed the Kit icon', 'Failed to change the Kit icon.');
		},
		async setKitDescription (kit: RunnitKit, description: string) {
			const trimmed = (description || '').trim();
			if (trimmed === kit.description) return;
			await this.updateKit(kit, { description: trimmed }, 'Changed the Kit blurb', 'Failed to change the Kit blurb.');
		},
		async setKitPublished (kit: RunnitKit, isPublished: boolean) {
			await this.updateKit(
				kit,
				{ isPublished, publishedAt: isPublished ? new Date() : null },
				isPublished ? 'Published ' + kit.label : kit.label + ' is a draft again',
				'Failed to change the Kit status.',
			);
		},
		async setKitDeleted (kit: RunnitKit, isDeleted: boolean) {
			if (isDeleted) {
				// eslint-disable-next-line no-alert
				if (!window.confirm('Delete the "' + kit.label + '" Kit? It stops showing anywhere and can be restored from the header.')) return;
			}
			await this.updateKit(
				kit,
				{ isDeleted, deletedAt: isDeleted ? new Date() : null },
				isDeleted ? 'Deleted ' + kit.label : 'Restored ' + kit.label,
				'Failed to change the Kit status.',
			);
			if (isDeleted && this.selectedKitId === kit.id) this.closeKit();
		},
		async toggleSurface (kit: RunnitKit, surface: string) {
			const surfaces: any = { ...(kit.surfaces || {}) };
			if (surfaces[surface]) {
				delete surfaces[surface];
			} else {
				surfaces[surface] = true;
			}
			// An empty map means every surface, so store null rather than {} to
			// keep "unset" and "none selected" the same thing.
			await this.updateKit(
				kit,
				{ surfaces: Object.keys(surfaces).length ? surfaces : null },
				'Changed where the Kit is shown',
				'Failed to change where the Kit is shown.',
			);
		},
		async setDefaultTool (kit: RunnitKit, nodeDefId: string) {
			await this.updateKit(
				kit,
				{ defaultNodeDefId: nodeDefId || null },
				nodeDefId ? this.toolTitle(nodeDefId) + ' is the default tool' : 'Cleared the default tool',
				'Failed to set the default tool.',
			);
		},

		// ── tool CRUD (whole-array rewrites) ──
		async addTool (nodeDefId: string) {
			if (!this.selectedKit) return;
			// Pre-tag with the active tab so a new entry cannot land untagged.
			const tags: any = {};
			if (this.activeTagTab) tags[this.activeTagTab] = true;
			const tools = this.selectedKit.tools.concat([{ nodeDefId, tags, labels: [] } as RunnitKitTool]);
			this.pickerOpen = false;
			await this.saveTools(
				this.selectedKit,
				tools,
				this.activeTagTab
					? 'Added ' + this.toolTitle(nodeDefId) + ' to ' + this.activeTagTab
					: 'Added ' + this.toolTitle(nodeDefId) + ', which still needs a Kit Tag',
				'Failed to add the tool.',
			);
		},
		async removeTool (kit: RunnitKit, nodeDefId: string) {
			const tools = kit.tools.filter((tool: RunnitKitTool) => tool.nodeDefId !== nodeDefId);
			const patch: any = {};
			// A Kit must not point its default at a tool it no longer contains.
			if (kit.defaultNodeDefId === nodeDefId) {
				patch.defaultNodeDefId = tools.length ? tools[0].nodeDefId : null;
			}
			if (this.inspectId === nodeDefId) this.inspectId = null;
			await this.saveTools(kit, tools, 'Removed ' + this.toolTitle(nodeDefId), 'Failed to remove the tool.', patch);
		},
		async moveTool (kit: RunnitKit, index: number, delta: number) {
			const tools = kit.tools.slice();
			const target = index + delta;
			if (target < 0 || target >= tools.length) return;
			const moved = tools[index];
			tools[index] = tools[target];
			tools[target] = moved;
			await this.saveTools(kit, tools, 'Reordered the tools', 'Failed to reorder the tools.');
		},
		async moveToolToTop (kit: RunnitKit, index: number) {
			if (index <= 0) return;
			const tools = kit.tools.slice();
			tools.unshift(tools.splice(index, 1)[0]);
			await this.saveTools(kit, tools, 'Moved the tool to the top', 'Failed to reorder the tools.');
		},
		async toggleTag (kit: RunnitKit, nodeDefId: string, tagKey: string) {
			const tool = kit.tools.find((entry: RunnitKitTool) => entry.nodeDefId === nodeDefId);
			if (!tool) return;
			if (tool.tags[tagKey]) {
				await this.removeTag(kit, nodeDefId, tagKey);
			} else {
				await this.addTag(kit, nodeDefId, tagKey);
			}
		},
		async addTag (kit: RunnitKit, nodeDefId: string, tagKey: string) {
			const key = (tagKey || '').trim();
			if (!key) return;
			const tools = this.mapTool(kit, nodeDefId, (tool: RunnitKitTool) => {
				const tags: any = { ...(tool.tags || {}) };
				tags[key] = true;
				return { ...tool, tags };
			});
			await this.saveTools(kit, tools, 'Tagged ' + this.toolTitle(nodeDefId) + ' ' + key, 'Failed to add the Kit Tag.');
		},
		async removeTag (kit: RunnitKit, nodeDefId: string, tagKey: string) {
			const tools = this.mapTool(kit, nodeDefId, (tool: RunnitKitTool) => {
				const tags: any = { ...(tool.tags || {}) };
				delete tags[tagKey];
				return { ...tool, tags };
			});
			await this.saveTools(kit, tools, 'Removed ' + tagKey + ' from ' + this.toolTitle(nodeDefId), 'Failed to remove the Kit Tag.');
		},
		addCustomTag (nodeDefId: string) {
			// eslint-disable-next-line no-alert
			const value = window.prompt('New Kit Tag. It becomes a filter tab as soon as a tool carries it.');
			if (value && value.trim()) this.addTag(this.selectedKit, nodeDefId, value.trim());
		},
		/**
		 * Kit Tags are derived, so renaming one means rewriting the key on every
		 * tool that carries it. The tab then re-derives under the new name.
		 */
		async renameTag (tagKey: string) {
			if (!this.selectedKit) return;
			// eslint-disable-next-line no-alert
			const value = window.prompt('Rename the "' + tagKey + '" Kit Tag on every tool in this Kit', tagKey);
			const next = (value || '').trim();
			if (!next || next === tagKey) return;

			const tools = this.selectedKit.tools.map((tool: RunnitKitTool) => {
				if (!tool.tags[tagKey]) return tool;
				const tags: any = { ...tool.tags };
				delete tags[tagKey];
				tags[next] = true;
				return { ...tool, tags };
			});
			await this.saveTools(this.selectedKit, tools, 'Renamed ' + tagKey + ' to ' + next, 'Failed to rename the Kit Tag.');
			this.activeTab = next;
		},
		async deleteTag (tagKey: string) {
			if (!this.selectedKit) return;
			const affected = this.selectedKit.tools.filter((tool: RunnitKitTool) => tool.tags[tagKey]).length;
			// eslint-disable-next-line no-alert
			if (!window.confirm('Remove the "' + tagKey + '" Kit Tag from ' + affected + ' tools? Any tool left with no tags becomes invisible to users.')) return;

			const tools = this.selectedKit.tools.map((tool: RunnitKitTool) => {
				if (!tool.tags[tagKey]) return tool;
				const tags: any = { ...tool.tags };
				delete tags[tagKey];
				return { ...tool, tags };
			});
			await this.saveTools(this.selectedKit, tools, 'Removed the ' + tagKey + ' tab', 'Failed to remove the Kit Tag.');
			this.setDefaultTab();
		},
		async toggleLabel (kit: RunnitKit, nodeDefId: string, presetKey: string) {
			const preset: KitToolLabel = KIT_TOOL_LABEL_PRESETS[presetKey as KitToolLabelPresetKey];
			const tool = kit.tools.find((entry: RunnitKitTool) => entry.nodeDefId === nodeDefId);
			if (!tool) return;

			if (this.hasLabel(tool, preset.id)) {
				await this.removeLabel(kit, nodeDefId, preset.id);
				return;
			}
			const tools = this.mapTool(kit, nodeDefId, (entry: RunnitKitTool) => ({
				...entry,
				labels: (entry.labels || []).concat([{ ...preset }]),
			}));
			await this.saveTools(kit, tools, 'Added the ' + preset.label + ' label', 'Failed to add the label.');
		},
		async removeLabel (kit: RunnitKit, nodeDefId: string, labelId: string) {
			const tools = this.mapTool(kit, nodeDefId, (tool: RunnitKitTool) => ({
				...tool,
				labels: (tool.labels || []).filter((label: KitToolLabel) => label.id !== labelId),
			}));
			await this.saveTools(kit, tools, 'Removed the label', 'Failed to remove the label.');
		},
		addCustomLabel (nodeDefId: string) {
			// eslint-disable-next-line no-alert
			const text = window.prompt('Label text, for example 4k');
			if (!text || !text.trim()) return;
			// eslint-disable-next-line no-alert
			const icon = window.prompt('Material Design Icons name for the label', 'mdi-star-outline');
			if (!icon || !icon.trim()) return;

			const label: KitToolLabel = {
				id: customKitToolLabelId(this.selectedKit.id, nodeDefId, text.trim()),
				icon: icon.trim(),
				label: text.trim(),
				value: null,
				type: 'HIGHLIGHT',
			};
			const tools = this.mapTool(this.selectedKit, nodeDefId, (tool: RunnitKitTool) => ({
				...tool,
				labels: (tool.labels || []).concat([label]),
			}));
			this.saveTools(this.selectedKit, tools, 'Added the ' + label.label + ' label', 'Failed to add the label.');
		},
		mapTool (kit: RunnitKit, nodeDefId: string, fn: (tool: RunnitKitTool) => RunnitKitTool): RunnitKitTool[] {
			return kit.tools.map((tool: RunnitKitTool) => (tool.nodeDefId === nodeDefId ? fn(tool) : tool));
		},

		// ── writes ──
		async saveTools (
			kit: RunnitKit,
			tools: RunnitKitTool[],
			message: string,
			errorMessage: string,
			extra: any = {},
		) {
			await this.updateKit(kit, { tools, ...extra }, message, errorMessage);
		},
		async updateKit (kit: RunnitKit, patch: any, message: string, errorMessage: string) {
			this.beginSave(message);
			try {
				await db.doc('runnitKits/' + kit.id).update(patch);
				await this.fetchKits();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, errorMessage);
			} finally {
				this.endSave();
			}
		},
		beginSave (message: string) {
			this.saving = true;
			this.saveState = 'saving';
			this.toast = message;
			clearTimeout(this.savedTimer);
			clearTimeout(this.toastTimer);
		},
		endSave () {
			this.saving = false;
			this.saveState = 'saved';
			this.savedTimer = setTimeout(() => { this.saveState = 'idle'; }, 2400);
			this.toastTimer = setTimeout(() => { this.toast = ''; }, 2800);
		},
		snack (status: string, message: string) {
			this.updateSnackbar({ status, message, show: true });
		},
	},
});
